/**
 * Giving the editing lock back as the page goes away.
 *
 * The ordinary release runs when the pane closes or the component unmounts.
 * Neither happens when somebody closes the tab, so the lock survives until its
 * TTL lapses and whoever wanted that employee next is told they are "locked by"
 * a person who has already gone.
 *
 * `pagehide` is the event that does fire there. What it cannot use is a normal
 * request: the browser is free to abandon anything still in flight as the page
 * dies. `keepalive` is the flag that asks it not to -- the same guarantee
 * `navigator.sendBeacon` gives, except that beacons cannot set headers and this
 * API authenticates with a bearer token, so a beacon would arrive as a 401.
 */

export type LockReleaseRequest = {
  targetUserId: number;
  sessionId: string;
  accessToken: string | null;
  /** The API origin, e.g. `https://example.com/api/v1` (no trailing slash). */
  baseUrl: string;
  /** Path of the release endpoint, relative to `baseUrl`. */
  path: string;
};

/**
 * Build the release call, or `null` when it could not be authenticated.
 *
 * Returned rather than sent so the decision is testable without a browser: the
 * caller does the one line of `fetch`.
 */
export function buildLockReleaseRequest({
  targetUserId,
  sessionId,
  accessToken,
  baseUrl,
  path,
}: LockReleaseRequest): [string, RequestInit] | null {
  // Without a token the request would only 401, and there is nobody left to
  // retry it -- the page is already going. The TTL is the fallback.
  if (!accessToken) {
    return null;
  }

  const url = `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  return [
    url,
    {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        acti_user: targetUserId,
        session_id: sessionId,
      }),
    },
  ];
}
